import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, Zap, MapPin, Battery, Navigation, Bell, Mail, MessageSquare, BellRing, Check, Trash2, Eye, EyeOff, User, Lock } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';
import { useNotificationPreferences, type NotificationChannel, type SeverityMin, type QuietHours } from '@/hooks/useNotificationPreferences';
import { TIMEZONES } from '@/lib/timezone';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToPush,
  revokePushSubscription,
  getCurrentPushSubscription,
  registerServiceWorker,
} from '@/lib/push';
import { PropertyTagsEditor } from '@/components/property/PropertyTagsEditor';
import { usePropertyTags } from '@/hooks/usePropertyTags';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';

interface PropertySettings {
  id: string;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string;
  battery_kwh_total: number;
  usable_fraction: number;
  baseline_load_w: number;
  hvac_threshold_add_watts: number;
  battery_reserve_pct: number;
  grid_mode: 'off_grid' | 'grid_primary' | null;
  electricity_rate_cents_per_kwh: number;
  system_cost_cents: number | null;
  system_install_date: string | null;
}

interface SystemSettings {
  id: string;
  display_name: string | null;
  device_sn: string | null;
  role: string;
  is_primary: boolean;
  is_active: boolean;
  plant_uuid: string | null;
}

export default function SettingsPage() {
  const {
    properties,
    plants,
    selectedPropertyId,
    selectedPlantId,
    refreshData,
    selectPlant,
    showHiddenPlants,
    setShowHiddenPlants,
    isInstaller,
  } = useApp();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [propertySettings, setPropertySettings] = useState<PropertySettings | null>(null);
  const [originalBatteryKwh, setOriginalBatteryKwh] = useState<number | null>(null);
  const [capacityChangeReason, setCapacityChangeReason] = useState('');
  const [systems, setSystems] = useState<SystemSettings[]>([]);
  const [plantToRemove, setPlantToRemove] = useState<{ id: string; display_name: string } | null>(null);
  const [removingPlant, setRemovingPlant] = useState(false);
  const [hidingPlantId, setHidingPlantId] = useState<string | null>(null);

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const propertyPlants = plants.filter(p => p.property_id === selectedPropertyId);
  const { tagsByPropertyId } = usePropertyTags(selectedPropertyId ? [selectedPropertyId] : []);
  const propertyTags = selectedPropertyId ? (tagsByPropertyId.get(selectedPropertyId) ?? []) : [];

  const setPlantHidden = async (plantId: string, isHidden: boolean) => {
    setHidingPlantId(plantId);
    try {
      const { error } = await supabase.from('plants').update({ is_hidden: isHidden }).eq('id', plantId);
      if (error) throw error;
      toast.success(isHidden ? 'Plant hidden' : 'Plant visible again');
      await refreshData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update visibility');
    } finally {
      setHidingPlantId(null);
    }
  };

  const handleRemovePlant = async () => {
    if (!plantToRemove) return;
    const propertyId = selectedPropertyId ?? null;
    const wasLastPlantOnProperty = propertyId != null && propertyPlants.length === 1;
    setRemovingPlant(true);
    try {
      const { error } = await supabase.from('plants').delete().eq('id', plantToRemove.id);
      if (error) throw error;
      if (wasLastPlantOnProperty && propertyId) {
        const { error: propErr } = await supabase.from('properties').delete().eq('id', propertyId);
        if (propErr) {
          console.error('[Settings] Failed to delete orphan property:', propErr);
        } else {
          toast.success('Property removed', { description: 'Last system was removed; property removed from fleet.' });
          setPlantToRemove(null);
          selectProperty(null);
          await refreshData();
          return;
        }
      }
      toast.success('Plant removed', { description: `${plantToRemove.display_name} has been removed. Equipment stays on the property.` });
      setPlantToRemove(null);
      if (selectedPlantId === plantToRemove.id) selectPlant(null);
      await refreshData();
    } catch (e) {
      toast.error('Could not remove plant', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setRemovingPlant(false);
    }
  };

  useEffect(() => {
    if (selectedProperty) {
      setPropertySettings({
        id: selectedProperty.id,
        name: selectedProperty.name,
        address: selectedProperty.address,
        latitude: selectedProperty.latitude,
        longitude: selectedProperty.longitude,
        timezone: selectedProperty.timezone,
        battery_kwh_total: selectedProperty.battery_kwh_total,
        usable_fraction: selectedProperty.usable_fraction,
        baseline_load_w: selectedProperty.baseline_load_w,
        hvac_threshold_add_watts: selectedProperty.hvac_threshold_add_watts,
        battery_reserve_pct: selectedProperty.battery_reserve_pct ?? 20,
        grid_mode: selectedProperty.grid_mode ?? null,
        electricity_rate_cents_per_kwh: selectedProperty.electricity_rate_cents_per_kwh ?? 16.5,
        system_cost_cents: selectedProperty.system_cost_cents ?? null,
        system_install_date: selectedProperty.system_install_date ?? null,
      });
      setOriginalBatteryKwh(selectedProperty.battery_kwh_total);
      setCapacityChangeReason('');

      // Fetch systems for this property
      fetchSystems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when selectedProperty changes; omit fetchSystems to avoid loop
  }, [selectedProperty]);

  const fetchSystems = async () => {
    if (!selectedPropertyId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('energy_systems')
        .select('id, display_name, device_sn, role, is_primary, is_active, plant_uuid')
        .eq('property_id', selectedPropertyId)
        .order('created_at');

      if (error) throw error;
      setSystems(data || []);
    } catch (err) {
      console.error('Error fetching systems:', err);
      toast.error('Failed to load systems');
    } finally {
      setLoading(false);
    }
  };

  const savePropertySettings = async () => {
    if (!propertySettings) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('properties')
        .update({
          name: propertySettings.name,
          address: propertySettings.address,
          latitude: propertySettings.latitude,
          longitude: propertySettings.longitude,
          timezone: propertySettings.timezone,
          battery_kwh_total: propertySettings.battery_kwh_total,
          usable_fraction: propertySettings.usable_fraction,
          baseline_load_w: propertySettings.baseline_load_w,
          hvac_threshold_add_watts: propertySettings.hvac_threshold_add_watts,
          battery_reserve_pct: propertySettings.battery_reserve_pct,
          reserve_source: 'installer_set',
          grid_mode: propertySettings.grid_mode,
          electricity_rate_cents_per_kwh: propertySettings.electricity_rate_cents_per_kwh,
          rate_source: 'manual',
          system_cost_cents: propertySettings.system_cost_cents,
          system_install_date: propertySettings.system_install_date,
        })
        .eq('id', propertySettings.id);

      if (error) throw error;

      // Audit battery capacity changes
      const batteryChanged = originalBatteryKwh !== null
        && propertySettings.battery_kwh_total !== originalBatteryKwh;
      if (batteryChanged) {
        const { error: auditError } = await supabase
          .from('battery_capacity_changes')
          .insert({
            property_id: propertySettings.id,
            changed_by: user?.id ?? null,
            previous_kwh: originalBatteryKwh,
            new_kwh: propertySettings.battery_kwh_total,
            change_source: 'settings_ui',
            reason: capacityChangeReason.trim() || null,
          });
        if (auditError) {
          console.error('Failed to log capacity change:', auditError);
          toast.error('Capacity saved but audit log failed. Please notify support.');
        }
        setOriginalBatteryKwh(propertySettings.battery_kwh_total);
        setCapacityChangeReason('');
      }

      toast.success('Property settings saved');
      refreshData();
    } catch (err) {
      console.error('Error saving property settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const geocodeAddress = async () => {
    if (!propertySettings?.address || !propertySettings?.id) {
      toast.error('No address to geocode');
      return;
    }

    setGeocoding(true);
    try {
      const { data, error } = await supabase.functions.invoke('geocode-address', {
        body: { propertyId: propertySettings.id },
      });

      if (data?.latitude != null && data?.longitude != null) {
        setPropertySettings({
          ...propertySettings,
          latitude: data.latitude,
          longitude: data.longitude,
          timezone: data.timezone || propertySettings.timezone,
        });
        toast.success(`Coordinates found: ${data.latitude.toFixed(4)}, ${data.longitude.toFixed(4)}`);
        refreshData();
        return;
      }

      const message = data?.error ?? (error?.message ?? 'Failed to geocode address');
      console.error('Geocode error:', message, { data, error });
      toast.error(typeof message === 'string' ? message : 'Failed to geocode address');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to geocode address';
      console.error('Error geocoding:', err);
      toast.error(message);
    } finally {
      setGeocoding(false);
    }
  };

  const updateSystemPrimary = async (systemId: string, isPrimary: boolean) => {
    try {
      // If setting as primary, unset other primaries first
      if (isPrimary) {
        await supabase
          .from('energy_systems')
          .update({ is_primary: false })
          .eq('property_id', selectedPropertyId);
      }

      const { error } = await supabase
        .from('energy_systems')
        .update({ is_primary: isPrimary })
        .eq('id', systemId);

      if (error) throw error;

      toast.success(isPrimary ? 'Set as primary system' : 'Removed primary status');
      fetchSystems();
    } catch (err) {
      console.error('Error updating system:', err);
      toast.error('Failed to update system');
    }
  };

  const updateSystemActive = async (systemId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('energy_systems')
        .update({ is_active: isActive })
        .eq('id', systemId);

      if (error) throw error;

      toast.success(isActive ? 'System activated' : 'System deactivated');
      fetchSystems();
    } catch (err) {
      console.error('Error updating system:', err);
      toast.error('Failed to update system');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">
          {selectedPropertyId
            ? `Configure ${selectedProperty?.name || 'property'} settings`
            : 'Configure your account and properties'}
        </p>
      </div>

      <Tabs
        defaultValue={
          !isInstaller && selectedPropertyId
            ? 'account'
            : selectedPropertyId
              ? 'property'
              : 'notifications'
        }
        className="space-y-6"
        key={selectedPropertyId ?? 'no-property'}
      >
        <TabsList className="bg-wt-bg-secondary">
          {!isInstaller && (
            <TabsTrigger value="account">
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
          )}
          <TabsTrigger value="property">
            <MapPin className="w-4 h-4 mr-2" />
            Property
          </TabsTrigger>
          <TabsTrigger value="battery">
            <Battery className="w-4 h-4 mr-2" />
            Battery
          </TabsTrigger>
          {isInstaller && (
            <TabsTrigger value="systems">
              <Zap className="w-4 h-4 mr-2" />
              Systems
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Account (homeowner only): Profile, Quiet Hours, Installer Contact */}
        {!isInstaller && (
          <TabsContent value="account" className="space-y-6">
            <AccountTab
              selectedPropertyId={selectedPropertyId}
              selectedProperty={selectedProperty}
              propertyTimezone={propertySettings?.timezone}
            />
          </TabsContent>
        )}

        {/* Property Settings */}
        <TabsContent value="property">
          {!selectedPropertyId ? (
            <Card className="bg-wt-bg-secondary border-border">
              <CardContent className="h-[200px] flex items-center justify-center">
                <p className="text-muted-foreground">Select a property to configure its settings</p>
              </CardContent>
            </Card>
          ) : (
          <>
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg">Property Details</CardTitle>
              <CardDescription>Basic property information and location</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {propertySettings && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Property Name</Label>
                      <Input
                        id="name"
                        value={propertySettings.name}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          name: e.target.value
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={propertySettings.address || ''}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          address: e.target.value
                        })}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="latitude">Latitude</Label>
                      <Input
                        id="latitude"
                        type="number"
                        step="0.0001"
                        value={propertySettings.latitude || ''}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          latitude: parseFloat(e.target.value) || null
                        })}
                        placeholder="Auto-detect from address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="longitude">Longitude</Label>
                      <Input
                        id="longitude"
                        type="number"
                        step="0.0001"
                        value={propertySettings.longitude || ''}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          longitude: parseFloat(e.target.value) || null
                        })}
                        placeholder="Auto-detect from address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select
                        value={propertySettings.timezone}
                        onValueChange={(value) => setPropertySettings({
                          ...propertySettings,
                          timezone: value
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((tz) => (
                            <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Geocode button when coordinates missing */}
                  {(!propertySettings.latitude || !propertySettings.longitude) && propertySettings.address && (
                    <div className="p-4 rounded-lg bg-status-stale/10 border border-status-stale/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-status-stale">Missing Coordinates</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Coordinates are required for accurate weather forecasts and sunrise/sunset times.
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={geocodeAddress}
                          disabled={geocoding}
                        >
                          {geocoding ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Navigation className="w-4 h-4 mr-2" />
                          )}
                          Geocode Address
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button onClick={savePropertySettings} disabled={saving}>
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Tags — editable from property settings */}
          {selectedPropertyId && selectedProperty && (
            <Card className="bg-wt-bg-secondary border-border mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Tags</CardTitle>
                <CardDescription>User-defined tags for filtering on the Properties page. Case-insensitive.</CardDescription>
              </CardHeader>
              <CardContent>
                <PropertyTagsEditor
                  propertyId={selectedPropertyId}
                  propertyName={selectedProperty.name}
                  initialTags={propertyTags}
                />
              </CardContent>
            </Card>
          )}
          </>
          )}
        </TabsContent>

        {/* Battery Settings */}
        <TabsContent value="battery">
          {!selectedPropertyId ? (
            <Card className="bg-wt-bg-secondary border-border">
              <CardContent className="h-[200px] flex items-center justify-center">
                <p className="text-muted-foreground">Select a property to configure battery settings</p>
              </CardContent>
            </Card>
          ) : (
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg">Battery Configuration</CardTitle>
              <CardDescription>Battery capacity and load modeling parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {propertySettings && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="battery_kwh">Total Battery Capacity (kWh)</Label>
                      <Input
                        id="battery_kwh"
                        type="number"
                        step="0.1"
                        value={propertySettings.battery_kwh_total}
                        readOnly={!isInstaller}
                        className={!isInstaller ? 'bg-wt-bg-tertiary' : undefined}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          battery_kwh_total: parseFloat(e.target.value) || 0
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Total nameplate capacity of all batteries physically installed at this property. Changes affect battery health calculations and degradation tracking. This does not change inverter or vendor settings.
                      </p>
                      {isInstaller && originalBatteryKwh !== null && propertySettings.battery_kwh_total !== originalBatteryKwh && (
                        <div className="space-y-1 pt-1">
                          <Label htmlFor="capacity_reason" className="text-xs">Reason for change (optional)</Label>
                          <Input
                            id="capacity_reason"
                            placeholder="e.g. corrected from spec sheet"
                            value={capacityChangeReason}
                            onChange={(e) => setCapacityChangeReason(e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="usable_fraction">Usable Fraction</Label>
                      <Input
                        id="usable_fraction"
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={propertySettings.usable_fraction}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          usable_fraction: parseFloat(e.target.value) || 0.8
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Fraction of capacity that's usable (0.8 = 80%)
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="baseline_load">Baseline Load (W)</Label>
                      <Input
                        id="baseline_load"
                        type="number"
                        value={propertySettings.baseline_load_w}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          baseline_load_w: parseInt(e.target.value) || 0
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Minimum expected load when HVAC is off
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hvac_threshold">HVAC Detection Threshold (W)</Label>
                      <Input
                        id="hvac_threshold"
                        type="number"
                        value={propertySettings.hvac_threshold_add_watts}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          hvac_threshold_add_watts: parseInt(e.target.value) || 800
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Load above baseline that indicates HVAC is running
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="battery_reserve_pct" title="Minimum battery level kept in reserve. Below this, the system may shut down to protect equipment.">
                        Reserve threshold (%)
                      </Label>
                      <Input
                        id="battery_reserve_pct"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={propertySettings.battery_reserve_pct}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          battery_reserve_pct: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Backup reserve — match this to your inverter's configured shutdown SOC.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Currently {((propertySettings.battery_reserve_pct / 100) * propertySettings.battery_kwh_total).toFixed(1)} kWh based on {propertySettings.battery_kwh_total} kWh total capacity.
                      </p>
                      {propertyPlants.some(p => p.provider_type === 'franklinwh') && !selectedProperty?.reserve_source && (
                        <p className="text-xs text-wt-amber-primary">
                          Set backup reserve to match your FranklinWH app setting.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grid_mode" title="Off-grid systems forecast overnight depletion. Grid-tied systems show backup readiness instead.">
                        Grid connection mode
                      </Label>
                      <Select
                        value={propertySettings.grid_mode ?? 'off_grid'}
                        onValueChange={(value) => setPropertySettings({
                          ...propertySettings,
                          grid_mode: value as 'off_grid' | 'grid_primary',
                        })}
                      >
                        <SelectTrigger id="grid_mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off_grid">Off-Grid</SelectItem>
                          <SelectItem value="grid_primary">Grid-Tied (Backup)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Off-grid systems forecast overnight depletion. Grid-tied systems show backup readiness instead.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="electricity_rate">
                        Electricity rate (cents/kWh)
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="electricity_rate"
                          type="number"
                          min={1}
                          max={100}
                          step={0.5}
                          className="w-32 font-mono"
                          value={propertySettings.electricity_rate_cents_per_kwh}
                          onChange={(e) => setPropertySettings({
                            ...propertySettings,
                            electricity_rate_cents_per_kwh: Math.min(100, Math.max(1, parseFloat(e.target.value) || 16.5)),
                          })}
                        />
                        <span className="text-sm text-muted-foreground">cents/kWh</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">
                          {selectedProperty?.rate_source === 'manual'
                            ? 'Manually set'
                            : selectedProperty?.rate_source === 'utility_lookup'
                            ? 'From utility lookup'
                            : selectedProperty?.rate_source === 'state_average'
                            ? `State average (${selectedProperty?.state_code ?? 'US'})`
                            : 'Default rate'}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Used for estimated savings and curtailment cost on Analytics. If unset, we use 16.5¢/kWh (US residential average).
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="system_cost">
                        System cost ($)
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="system_cost"
                          type="number"
                          min={0}
                          step={100}
                          placeholder="e.g. 25000"
                          className="w-40 font-mono"
                          value={propertySettings.system_cost_cents != null ? (propertySettings.system_cost_cents / 100).toString() : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPropertySettings({
                              ...propertySettings,
                              system_cost_cents: val === '' ? null : Math.round(parseFloat(val) * 100),
                            });
                          }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Total system cost before incentives. Used to track payback progress on Analytics.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="system_install_date">
                        System install date
                      </Label>
                      <Input
                        id="system_install_date"
                        type="date"
                        className="w-48 font-mono"
                        value={propertySettings.system_install_date ?? ''}
                        onChange={(e) => setPropertySettings({
                          ...propertySettings,
                          system_install_date: e.target.value || null,
                        })}
                      />
                      <p className="text-xs text-muted-foreground">
                        When the solar system was installed. Shown on the payback tracker.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={savePropertySettings} disabled={saving}>
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {isInstaller && (
        <>
        {/* Systems Settings */}
        <TabsContent value="systems">
          {!selectedPropertyId ? (
            <Card className="bg-wt-bg-secondary border-border">
              <CardContent className="h-[200px] flex items-center justify-center">
                <p className="text-muted-foreground">Select a property to manage systems</p>
              </CardContent>
            </Card>
          ) : (
          <>
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Energy Systems</CardTitle>
                  <CardDescription>Manage inverters and equipment</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-[200px]">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : systems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                  <Zap className="w-12 h-12 mb-4 opacity-30" />
                  <p>No systems configured</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {systems.map((system) => {
                    const plant = propertyPlants.find(p => p.id === system.plant_uuid);
                    
                    return (
                      <div 
                        key={system.id}
                        className={`p-4 rounded-lg border ${
                          system.is_active 
                            ? 'bg-secondary/30 border-border' 
                            : 'bg-secondary/10 border-border/50 opacity-60'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Zap className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                {system.display_name || system.device_sn || 'Unnamed System'}
                                {system.is_primary && (
                                  <Badge variant="default" className="text-xs">Primary</Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-2">
                                {system.device_sn && <span>SN: {system.device_sn}</span>}
                                <Badge variant="outline" className="text-xs">{system.role}</Badge>
                                {plant && <span>Plant: {plant.display_name}</span>}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`primary-${system.id}`}
                                checked={system.is_primary}
                                onCheckedChange={(checked) => updateSystemPrimary(system.id, checked)}
                              />
                              <Label htmlFor={`primary-${system.id}`} className="text-sm">
                                Primary
                              </Label>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`active-${system.id}`}
                                checked={system.is_active}
                                onCheckedChange={(checked) => updateSystemActive(system.id, checked)}
                              />
                              <Label htmlFor={`active-${system.id}`} className="text-sm">
                                Active
                              </Label>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Plants Section */}
          <Card className="bg-wt-bg-secondary border-border mt-6">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg">Plants</CardTitle>
                  <CardDescription>Logical groupings of equipment</CardDescription>
                </div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showHiddenPlants}
                    onChange={(e) => setShowHiddenPlants(e.target.checked)}
                    className="rounded border-input"
                  />
                  Show hidden plants
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {propertyPlants.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[100px] text-muted-foreground">
                  <p>No plants configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {propertyPlants.map((plant) => {
                    const plantSystems = systems.filter(s => s.plant_uuid === plant.id);

                    return (
                      <div
                        key={plant.id}
                        className={`p-4 rounded-lg bg-secondary/30 border border-border ${plant.is_hidden ? 'opacity-75' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {plant.display_name}
                              {plant.is_hidden && (
                                <span className="text-xs font-normal text-muted-foreground">Hidden</span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {plantSystems.length} system{plantSystems.length !== 1 ? 's' : ''} attached
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <ProviderBadge provider={plant.provider_type || 'manual'} />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
                              onClick={() => setPlantHidden(plant.id, !plant.is_hidden)}
                              disabled={!!hidingPlantId}
                              title={plant.is_hidden ? 'Unhide plant' : 'Hide plant'}
                            >
                              {hidingPlantId === plant.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : plant.is_hidden ? (
                                <Eye className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                              onClick={() => setPlantToRemove({ id: plant.id, display_name: plant.display_name })}
                              title="Remove plant"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          </>
          )}
        </TabsContent>
        </>
        )}

        {/* Notification preferences (Phase 7) */}
        <TabsContent value="notifications" className="space-y-6">
          {isInstaller && (
            <p className="text-sm text-muted-foreground">
              Default notification email and phone are managed in{' '}
              <Link to="/app/settings" className="text-primary underline">Settings</Link>
              {' '}→ Account.
            </p>
          )}
          <AccountNotificationContact />
          <NotificationPreferencesTab propertyId={selectedPropertyId} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!plantToRemove} onOpenChange={(open) => !open && setPlantToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {plantToRemove && propertyPlants.length === 1 ? 'Remove property?' : 'Remove plant?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {plantToRemove && (
                <>
                  {propertyPlants.length === 1 ? (
                    <>
                      This is the only system on this property. Removing it will remove the property from your fleet.
                      This cannot be undone.
                    </>
                  ) : (
                    <>
                      This will remove <strong>{plantToRemove.display_name}</strong> from this property.
                      Equipment (inverters) will stay on the property but will no longer be grouped under this plant.
                      This cannot be undone.
                    </>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingPlant}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRemovePlant();
              }}
              disabled={removingPlant}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removingPlant ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing…
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Homeowner-only Account tab: Profile, Quiet Hours, Installer Contact. */
function AccountTab({
  selectedPropertyId,
  selectedProperty,
  propertyTimezone,
}: {
  selectedPropertyId: string | null;
  selectedProperty: { name: string; org_id: string } | undefined;
  propertyTimezone: string | undefined;
}) {
  const { user } = useAuth();
  const { organization, userProfile, refreshData } = useApp();
  const { preferences, isLoading: prefsLoading, upsert: upsertPref, isUpserting: prefsUpserting } = useNotificationPreferences(selectedPropertyId);

  const [passwordModal, setPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [fullName, setFullName] = useState(userProfile?.full_name ?? '');
  const [fullNameSaving, setFullNameSaving] = useState(false);
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState('22:00');
  const [quietEnd, setQuietEnd] = useState('07:00');
  const [quietTz, setQuietTz] = useState(propertyTimezone ?? 'America/Chicago');
  const [quietSaving, setQuietSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setFullName(userProfile?.full_name ?? '');
  }, [userProfile?.full_name]);

  const inAppPref = selectedPropertyId
    ? preferences.find((p) => p.channel === 'in_app' && p.property_id === selectedPropertyId)
    : null;
  const quietHoursFromPref = inAppPref?.quiet_hours as QuietHours | null | undefined;
  const orgQuietHours = organization?.default_quiet_hours as QuietHours | null | undefined;

  useEffect(() => {
    if (prefsLoading) return;
    if (quietHoursFromPref) {
      setQuietEnabled(true);
      setQuietStart(quietHoursFromPref.start ?? '22:00');
      setQuietEnd(quietHoursFromPref.end ?? '07:00');
      setQuietTz(quietHoursFromPref.timezone ?? propertyTimezone ?? 'America/Chicago');
    } else if (orgQuietHours) {
      setQuietEnabled(false);
      setQuietStart(orgQuietHours.start ?? '22:00');
      setQuietEnd(orgQuietHours.end ?? '07:00');
      setQuietTz(orgQuietHours.timezone ?? propertyTimezone ?? 'America/Chicago');
    } else {
      setQuietTz(propertyTimezone ?? 'America/Chicago');
    }
  }, [prefsLoading, quietHoursFromPref, orgQuietHours, propertyTimezone]);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated');
      setPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSaveFullName = async () => {
    setFullNameSaving(true);
    try {
      const { error } = await supabase
        .from('users_profile')
        .update({ full_name: fullName.trim() || null })
        .eq('user_id', user?.id);
      if (error) throw error;
      toast.success('Name saved');
      await refreshData();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save name');
    } finally {
      setFullNameSaving(false);
    }
  };

  const handleSaveQuietHours = async () => {
    if (!selectedPropertyId || !user?.id) return;
    setQuietSaving(true);
    try {
      const quietHours: QuietHours | null = quietEnabled
        ? { start: quietStart, end: quietEnd, timezone: quietTz }
        : null;
      await upsertPref({
        channel: 'in_app',
        property_id: selectedPropertyId,
        severity_min: inAppPref?.severity_min ?? 'info',
        enabled: inAppPref?.enabled ?? true,
        quiet_hours: quietHours,
      });
      toast.success('Quiet hours saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save quiet hours');
    } finally {
      setQuietSaving(false);
    }
  };

  const { signOut } = useAuth();

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await invokeAuthedFunction(supabase, 'delete-account', { confirmText: 'DELETE' });
      toast.success('Account deleted');
      await signOut();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const nc = organization?.notification_contact as { email?: string; phone?: string } | null | undefined;
  const bp = organization?.brand_profile as { website?: string | null } | null | undefined;
  const installerWebsite = bp?.website ?? null;
  const installerTimezone = organization?.default_timezone ?? null;
  const hasContact = nc?.email || nc?.phone || installerWebsite;

  return (
    <>
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-lg">Profile</CardTitle>
          <CardDescription>Account email and display name.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={user?.email ?? ''} readOnly className="bg-wt-bg-tertiary max-w-md" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-full-name">Full name</Label>
            <Input
              id="account-full-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your name"
              className="max-w-md"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveFullName} disabled={fullNameSaving}>
              {fullNameSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save name
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPasswordModal(true)}>
              <Lock className="w-3.5 h-3.5 mr-1.5" />
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-lg">Quiet hours</CardTitle>
          <CardDescription>
            Suppress non-critical notifications during this window. If your installer set a default, it appears below when you have not set your own.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {orgQuietHours && !quietHoursFromPref && (
            <p className="text-xs text-muted-foreground">
              Default: {orgQuietHours.start}–{orgQuietHours.end} ({orgQuietHours.timezone}) (set by installer)
            </p>
          )}
          <div className="flex items-center justify-between">
            <Label>Enable quiet hours</Label>
            <Switch checked={quietEnabled} onCheckedChange={setQuietEnabled} />
          </div>
          {quietEnabled && (
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div className="space-y-2">
                <Label>Start</Label>
                <Input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>End</Label>
                <Input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Timezone</Label>
                <Select value={quietTz} onValueChange={setQuietTz}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <Button onClick={handleSaveQuietHours} disabled={quietSaving || prefsUpserting}>
            {quietSaving || prefsUpserting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-lg">Installer contact</CardTitle>
          <CardDescription>Who manages your system. Contact them for support.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {organization?.brand_profile?.logo_url && (
            <img src={organization.brand_profile.logo_url} alt="" className="h-10 object-contain" />
          )}
          <p className="font-medium text-foreground">{organization?.name ?? '—'}</p>
          {hasContact ? (
            <div className="text-sm text-muted-foreground space-y-1">
              {nc?.email && (
                <p>Email: <a href={`mailto:${nc.email}`} className="text-wt-accent hover:underline">{nc.email}</a></p>
              )}
              {nc?.phone && (
                <p>Phone: <a href={`tel:${nc.phone}`} className="text-wt-accent hover:underline">{nc.phone}</a></p>
              )}
              {installerWebsite && (
                <p>Website: <a href={installerWebsite.startsWith('http') ? installerWebsite : `https://${installerWebsite}`} target="_blank" rel="noopener noreferrer" className="text-wt-accent hover:underline">{installerWebsite.replace(/^https?:\/\//, '')}</a></p>
              )}
              {installerTimezone && (
                <p className="text-xs text-muted-foreground/70 mt-2">Business hours: {installerTimezone.replace(/_/g, ' ')}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Ask your installer to update their contact information.</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-wt-bg-secondary border-destructive/30">
        <CardHeader>
          <CardTitle className="text-lg text-destructive">Delete account</CardTitle>
          <CardDescription>Permanently delete your account and personal data. Your property data remains available to your installer.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete my account
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteConfirmText(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your account and all your personal data. This cannot be undone.
              Your property data and system telemetry will remain available to your installer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-2">
            <Label>Type DELETE to confirm</Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="max-w-xs"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {passwordModal && (
        <Dialog open={passwordModal} onOpenChange={setPasswordModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>Enter your new password. You will stay signed in.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="space-y-2">
                <Label>Confirm password</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPasswordModal(false)}>Cancel</Button>
              <Button onClick={handleChangePassword} disabled={passwordSaving || !newPassword || newPassword !== confirmPassword}>
                {passwordSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update password
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function channelLabel(channel: NotificationChannel): string {
  switch (channel) {
    case 'email': return 'Email';
    case 'sms': return 'SMS';
    case 'web_push': return 'Web Push';
    default: return channel;
  }
}

/** Account-level notification contact: email and phone (used for email/SMS delivery). */
function AccountNotificationContact() {
  const { user } = useAuth();
  const { preferences, isLoading, upsert, isUpserting } = useNotificationPreferences(null);
  const [saving, setSaving] = useState(false);
  const emailPref = preferences.find((p) => p.channel === 'email' && p.property_id == null);
  const smsPref = preferences.find((p) => p.channel === 'sms' && p.property_id == null);
  const defaultEmail = user?.email ?? '';
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  useEffect(() => {
    if (isLoading) return;
    setEmail(emailPref?.email_override ?? defaultEmail);
    setPhone(smsPref?.phone_for_sms ?? '');
  }, [isLoading, emailPref?.email_override, smsPref?.phone_for_sms, defaultEmail]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsert({
        channel: 'email',
        property_id: null,
        severity_min: emailPref?.severity_min ?? 'warning',
        enabled: emailPref?.enabled ?? true,
        email_override: email.trim() || null,
      });
      await upsert({
        channel: 'sms',
        property_id: null,
        severity_min: smsPref?.severity_min ?? 'warning',
        enabled: smsPref?.enabled ?? false,
        phone_for_sms: phone.trim() || null,
      });
      toast.success('Notification contact saved');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="h-[200px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader>
        <CardTitle className="text-lg">Notification contact</CardTitle>
        <CardDescription>
          Email and phone used for alert delivery. Leave blank to use your account email for email notifications.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="account-notification-email">Notification email</Label>
          <Input
            id="account-notification-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={defaultEmail || 'you@example.com'}
          />
          <p className="text-xs text-muted-foreground">
            Alerts are sent to this address when email notifications are enabled. Defaults to your account email if blank.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="account-notification-phone">Phone (for SMS)</Label>
          <Input
            id="account-notification-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 234 567 8900"
          />
          <p className="text-xs text-muted-foreground">
            Optional. Used for SMS alerts when SMS is enabled for your account.
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || isUpserting}>
            {saving || isUpserting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function NotificationPreferencesTab({ propertyId }: { propertyId: string | null }) {
  const { preferences, isLoading, upsert, isUpserting } = useNotificationPreferences(propertyId);
  const { session } = useAuth();
  const [saving, setSaving] = useState(false);
  const [pushSubscribing, setPushSubscribing] = useState(false);
  const [pushRevoking, setPushRevoking] = useState(false);
  const [pushSubscription, setPushSubscription] = useState<PushSubscription | null>(null);
  const [criticalOptOutDialogOpen, setCriticalOptOutDialogOpen] = useState(false);
  const [criticalOptOutChannel, setCriticalOptOutChannel] = useState<NotificationChannel | null>(null);
  const [criticalOptOutAcknowledged, setCriticalOptOutAcknowledged] = useState(false);
  const [smsConsentDialogOpen, setSmsConsentDialogOpen] = useState(false);
  const [smsConsentAcknowledged, setSmsConsentAcknowledged] = useState(false);

  const getPref = (channel: NotificationChannel) =>
    preferences.find((p) => p.channel === channel && (propertyId ? p.property_id === propertyId : p.property_id == null))
    ?? preferences.find((p) => p.channel === channel);

  const emailPref = getPref('email');
  const smsPref = getPref('sms');
  const webPushPref = getPref('web_push');
  const accountSmsPref = preferences.find((p) => p.channel === 'sms' && p.property_id == null);
  const hasPhone = !!accountSmsPref?.phone_for_sms?.trim();

  useEffect(() => {
    if (!propertyId) return;
    registerServiceWorker().then(() => {});
    getCurrentPushSubscription().then(setPushSubscription);
  }, [propertyId]);

  const handleChannelToggle = async (channel: NotificationChannel, enabled: boolean) => {
    if (channel === 'sms' && enabled && !smsPref?.sms_consent_at) {
      setSmsConsentAcknowledged(false);
      setSmsConsentDialogOpen(true);
      return;
    }
    const severityMin = channel === 'email' ? emailPref?.severity_min
      : channel === 'sms' ? smsPref?.severity_min
      : channel === 'web_push' ? webPushPref?.severity_min
      : 'warning';
    if (!enabled && severityMin === 'critical') {
      setCriticalOptOutChannel(channel);
      setCriticalOptOutAcknowledged(false);
      setCriticalOptOutDialogOpen(true);
      return;
    }
    setSaving(true);
    try {
      await upsert({
        channel,
        property_id: propertyId ?? null,
        severity_min: severityMin ?? 'warning',
        enabled,
      });
      toast.success(enabled ? `${channelLabel(channel)} notifications enabled` : `${channelLabel(channel)} notifications disabled`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const handleSmsConsentConfirm = async () => {
    if (!smsConsentAcknowledged) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await upsert({
        channel: 'sms',
        property_id: propertyId ?? null,
        severity_min: smsPref?.severity_min ?? 'warning',
        enabled: true,
        sms_consent_at: now,
      });
      toast.success('SMS notifications enabled');
      setSmsConsentDialogOpen(false);
      setSmsConsentAcknowledged(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to enable SMS');
    } finally {
      setSaving(false);
    }
  };

  const handleCriticalOptOutConfirm = async () => {
    if (!criticalOptOutChannel || !criticalOptOutAcknowledged) return;
    setSaving(true);
    try {
      const severityMin = criticalOptOutChannel === 'email' ? emailPref?.severity_min
        : criticalOptOutChannel === 'sms' ? smsPref?.severity_min
        : criticalOptOutChannel === 'web_push' ? webPushPref?.severity_min
        : 'warning';
      await upsert({
        channel: criticalOptOutChannel,
        property_id: propertyId ?? null,
        severity_min: severityMin ?? 'critical',
        enabled: false,
        critical_opt_out_acknowledged_at: new Date().toISOString(),
      });
      toast.success(`${channelLabel(criticalOptOutChannel)} critical alerts disabled. You will still see alerts in-app.`);
      setCriticalOptOutDialogOpen(false);
      setCriticalOptOutChannel(null);
      setCriticalOptOutAcknowledged(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const handleSeverityChange = async (channel: NotificationChannel, severity_min: SeverityMin) => {
    setSaving(true);
    try {
      const enabled = channel === 'email' ? (emailPref?.enabled ?? true)
        : channel === 'sms' ? (smsPref?.enabled ?? false)
        : (webPushPref?.enabled ?? false);
      await upsert({
        channel,
        property_id: propertyId ?? null,
        severity_min,
        enabled,
      });
      toast.success('Preference saved');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const handlePushSubscribe = async () => {
    if (!session?.access_token || !propertyId) return;
    setPushSubscribing(true);
    try {
      const result = await subscribeToPush(session.access_token, {
        property_id: propertyId,
        severity_min: webPushPref?.severity_min ?? 'warning',
      });
      if (result.ok) {
        const sub = await getCurrentPushSubscription();
        setPushSubscription(sub);
        toast.success('Web Push enabled on this device');
      } else {
        toast.error(result.error ?? 'Failed to subscribe');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to subscribe');
    } finally {
      setPushSubscribing(false);
    }
  };

  const handlePushRevoke = async () => {
    if (!session?.access_token) return;
    setPushRevoking(true);
    try {
      const result = await revokePushSubscription(session.access_token);
      setPushSubscription(null);
      if (result.ok) toast.success('Web Push disabled on this device');
      else toast.error(result.error ?? 'Failed to unsubscribe');
    } catch (err) {
      console.error(err);
      toast.error('Failed to unsubscribe');
    } finally {
      setPushRevoking(false);
    }
  };

  if (!propertyId) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-8 space-y-2 text-center">
          <p className="text-muted-foreground">Select a property to manage per-property channel preferences (email, SMS, web push).</p>
          <p className="text-sm text-muted-foreground">Your default notification contact is set in the card above.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="h-[200px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader>
        <CardTitle className="text-lg">Notification preferences</CardTitle>
        <CardDescription>
          Choose how you receive alerts for this property. In-app notifications are always on. Default contact (email and phone) is set in the card above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Email</p>
                <p className="text-sm text-muted-foreground">Receive critical and warning alerts by email</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Select
                value={emailPref?.severity_min ?? 'warning'}
                onValueChange={(v) => handleSeverityChange('email', v as SeverityMin)}
                disabled={saving || isUpserting}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical only</SelectItem>
                  <SelectItem value="warning">Warning +</SelectItem>
                  <SelectItem value="info">All</SelectItem>
                </SelectContent>
              </Select>
              <Switch
                checked={emailPref?.enabled ?? true}
                onCheckedChange={(checked) => handleChannelToggle('email', checked)}
                disabled={saving || isUpserting}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">SMS</p>
                  <p className="text-sm text-muted-foreground">Receive critical and warning alerts by text message</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Select
                  value={smsPref?.severity_min ?? 'warning'}
                  onValueChange={(v) => handleSeverityChange('sms', v as SeverityMin)}
                  disabled={saving || isUpserting || !hasPhone}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical only</SelectItem>
                    <SelectItem value="warning">Warning +</SelectItem>
                    <SelectItem value="info">All</SelectItem>
                  </SelectContent>
                </Select>
                <Switch
                  checked={smsPref?.enabled ?? false}
                  onCheckedChange={(checked) => handleChannelToggle('sms', checked)}
                  disabled={saving || isUpserting || !hasPhone}
                />
              </div>
            </div>
            {!hasPhone && (
              <p className="text-xs text-muted-foreground">Add a phone number above to enable SMS alerts.</p>
            )}
          </div>

          <div className="flex flex-col gap-4 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BellRing className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Web Push</p>
                  <p className="text-sm text-muted-foreground">
                    Critical alerts, overnight risk, and preservation advisories. Opt-in only; no auto-prompting.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Select
                  value={webPushPref?.severity_min ?? 'warning'}
                  onValueChange={(v) => handleSeverityChange('web_push', v as SeverityMin)}
                  disabled={saving || isUpserting}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical only</SelectItem>
                    <SelectItem value="warning">Warning +</SelectItem>
                    <SelectItem value="info">All</SelectItem>
                  </SelectContent>
                </Select>
                <Switch
                  checked={webPushPref?.enabled ?? false}
                  onCheckedChange={(checked) => handleChannelToggle('web_push', checked)}
                  disabled={saving || isUpserting}
                />
              </div>
            </div>
            {webPushPref?.enabled && (
              <div className="flex items-center gap-2 border-t pt-4">
                {pushSubscription ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePushRevoke}
                    disabled={pushRevoking}
                  >
                    {pushRevoking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Unsubscribe on this device
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handlePushSubscribe}
                    disabled={pushSubscribing || !session}
                  >
                    {pushSubscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Subscribe on this device
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <AlertDialog open={criticalOptOutDialogOpen} onOpenChange={(open) => { setCriticalOptOutDialogOpen(open); if (!open) setCriticalOptOutChannel(null); setCriticalOptOutAcknowledged(false); }}>
        <AlertDialogContent>
          <AlertDialogTitle>Disable critical safety alerts?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                You are disabling critical safety alerts for {criticalOptOutChannel ? channelLabel(criticalOptOutChannel) : 'this channel'}. You will still see alerts in-app. Watt Tower and your installer are not liable for missed notifications on this channel.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  id="critical-opt-out-ack"
                  checked={criticalOptOutAcknowledged}
                  onCheckedChange={(c) => setCriticalOptOutAcknowledged(c === true)}
                />
                <label htmlFor="critical-opt-out-ack" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  I understand and accept this risk
                </label>
              </div>
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCriticalOptOutConfirm} disabled={!criticalOptOutAcknowledged}>
              Disable critical alerts
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={smsConsentDialogOpen} onOpenChange={(open) => { setSmsConsentDialogOpen(open); if (!open) setSmsConsentAcknowledged(false); }}>
        <AlertDialogContent>
          <AlertDialogTitle>SMS alerts</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                By enabling SMS alerts, you agree to receive text messages from Watt Tower at the number above. Message and data rates may apply. You can disable SMS at any time.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  id="sms-consent-ack"
                  checked={smsConsentAcknowledged}
                  onCheckedChange={(c) => setSmsConsentAcknowledged(c === true)}
                />
                <label htmlFor="sms-consent-ack" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  I agree to receive SMS alerts
                </label>
              </div>
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSmsConsentConfirm} disabled={!smsConsentAcknowledged || saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enable SMS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}


